import { useState, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Activity,
  FileText,
  Settings,
  CreditCard,
  FileCheck,
  LogOut,
  ChevronDown,
  Bot,
  Target,
  CheckCircle2,
  Sliders,
  Users,
  ShieldAlert,
  BarChart3,
  Calendar,
  Building2,
  Check,
  Plus,
  ChevronRight,
  Link2,
  Network,
  ShieldCheck,
  FileSignature,
  Lock,
  Eye,
  Package,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { SignOutDialog } from "./SignOutDialog";
import { CreateOrgModal } from "./CreateOrgModal";
import { Tooltip } from "./ui/tooltip";
import { toast } from "sonner";
import { useCurrentUser, useOrganization, useUserOrgs, useSwitchOrg, useLogout, useBilling } from "../../hooks/useAPI";
import { planMeets, planTooltip, PLAN_NAMES, type RequiredPlan } from "./PlanLock";
import { TokenManager } from "../../services/apiClient";

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

// Minimal org-row shape for the avatar + dropdown row. The full Organization
// type lives in types/api.ts; we only need name + optional logo + id here,
// and the `_id ?? id` fallback covers Mongo-vs-API key drift.
interface OrgRow {
  id?: string;
  _id?: string;
  name?: string;
  logoUrl?: string | null;
}
function getOrgId(o: OrgRow | undefined | null): string | undefined {
  return o?._id ?? o?.id;
}

function OrgAvatar({ org, size = 28 }: { org: OrgRow | null | undefined; size?: number }) {
  const name: string = org?.name ?? '';
  const logoUrl: string | null = org?.logoUrl ?? null;
  const initial = name[0]?.toUpperCase() ?? '?';
  return (
    <div
      className="flex items-center justify-center border border-border bg-surface-2 flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {logoUrl
        ? <img src={logoUrl} alt="" className="w-full h-full object-cover" />
        : <span className="text-xs font-bold text-mono text-foreground">{initial}</span>
      }
    </div>
  );
}

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  isDemoMode?: boolean;
  onViewChange?: (viewId: string) => void;
  demoCurrentView?: string;
}

const SIDEBAR_WIDTH_KEY  = 'sidebar-width';
const ICON_ONLY_WIDTH    = 48;    // px — icon-only state
const ICON_THRESHOLD     = 100;   // px — below this → show icons; on release → snap to icon-only
const DEFAULT_WIDTH      = 220;   // px
const MAX_WIDTH_RATIO    = 0.25;  // 25% of window width

const demoHiddenItems  = ['super-admin'];
const demoDisabledItems: string[] = [];

// ── NEW Navigation groups (IA reorg 2026-05-30) ────────────────────────
// The new structure uses separate groups for Overview, Operate/Integrations,
// Governance, Audit & Proof, Compliance (domain hubs), Insights, and Admin.
// Legacy routes kept in a collapsible "Alt" group at bottom (default collapsed).
// All NEW items route to /dashboard/<slug> (clean routes, no n/ prefix).
// All OLD items route to /dashboard/o/<original-path> (under o/ parent).

// ── OVERVIEW ───────────────────────────────────────────────────────────
const overviewItems = [
  { id: 'overview', label: 'nav.overview', icon: LayoutDashboard },
];

// ── OPERATIONS / INTEGRATIONS ──────────────────────────────────────────
const operateItems = [
  { id: 'systems',      label: 'nav.systems',      icon: Bot },
  { id: 'connectors',   label: 'nav.connectors',   icon: Network },
  { id: 'decision-log', label: 'nav.decisionLog',  icon: FileText },
  { id: 'review-queue', label: 'nav.reviewQueue',  icon: CheckCircle2 },
  { id: 'anomalies',    label: 'nav.anomalies',    icon: Activity },
];

// ── AUDIT & PROOF (the wedge: prove every AI decision) ──────────────────
const auditProofItems = [
  { id: 'audit-chain',       label: 'nav.auditChain',      icon: Link2 },
  { id: 'provenance',        label: 'nav.provenance',      icon: FileSignature },
  { id: 'evidence-exports',  label: 'nav.evidenceExports', icon: Package },
  // incidents + erasure moved to Compliance 2026-06-03: they are compliance/
  // rights workflows that TOUCH the audit trail, not proof primitives. Audit &
  // Proof = the 3 proof primitives only (record integrity · output provenance ·
  // proof delivery).
];

// ── GOVERNANCE (policy + fairness) ─────────────────────────────────────
const governanceItems = [
  { id: 'policy-engine',           label: 'nav.policyEngine',           icon: Sliders },
  { id: 'calibration-confidence',  label: 'nav.calibrationConfidence',  icon: Target },
  { id: 'fairness-bias',           label: 'nav.fairnessBias',           icon: Activity },
];

// ── COMPLIANCE HUBS — IA-Modell locked 2026-06-03 ────────────────────────
// 9 Pages = compliance-overview (cross-framework COCKPIT: Status/Obligations/
// Incidents tabs) + 8 framework hubs (each multi-tab). NOT in this nav (fold
// into hub tabs at build; reachable via URL until then):
//   incidents → overview "Incidents" tab · erasure → gdpr "Löschung" tab ·
//   dora-register → dora "Register" tab.
// (The /dashboard/{incidents,erasure} routes currently bridge to the legacy
//  full pages — that's why they look "filled"; they get rebuilt as tabs.)
// Solvency II + VAG = ONE hub (VAG = German transposition of the Solvency II
// directive; ORSA/governance live on both levels → one regime, one page).
const complianceHubItems = [
  { id: 'compliance-overview', label: 'nav.complianceOverview', icon: LayoutDashboard },
  // Horizontal frameworks (apply across insurance + finance)
  { id: 'eu-ai-act',          label: 'nav.euAiAct',           icon: FileText },
  { id: 'gdpr',               label: 'nav.gdpr',              icon: FileText },
  { id: 'dora',               label: 'nav.dora',              icon: FileText },
  { id: 'aml-gwg',            label: 'nav.amlGwg',            icon: FileText },
  { id: 'data-act-fida',      label: 'nav.dataActFida',       icon: FileText },
  // Versicherer-specific frameworks
  { id: 'solvency-vag',       label: 'nav.solvencyVag',       icon: FileText },
  { id: 'beratung',           label: 'nav.beratung',          icon: FileText },
  { id: 'eiopa',              label: 'nav.eiopa',             icon: FileText },
];

// ── INSIGHTS / ANALYTICS ────────────────────────────────────────────────
const insightsItems = [
  { id: 'analytics', label: 'nav.analytics', icon: BarChart3 },
  { id: 'reports',   label: 'nav.reports',   icon: Calendar },
];

// ── ADMIN ──────────────────────────────────────────────────────────────
// Admin = account/org administration (people · money+usage · org-service keys ·
// account audit trail · org settings). IA locked 2026-06-03 = 5 slots.
// api-keys here = ORG-service keys (adj_live_) only; adj_system_ lives at Systems,
// connector-creds at Connectors. activity-log = the account/security audit trail
// (NOT the AI-decision audit-chain, which is Audit & Proof).
// 2026-06-13: aus /dashboard/o auf Default-Routen migriert (IDs ohne o/-Präfix
// → /dashboard/<id>). API-Schlüssel ist kein eigener Eintrag mehr — wird ein
// Tab in den Einstellungen (Integration-Hub-Stub war ausgeweidet).
const adminItems = [
  { id: 'team',         label: 'nav.team',     icon: Users },
  { id: 'billing',      label: 'nav.billing',  icon: CreditCard },
  { id: 'activity-log', label: 'Activity Log', icon: FileCheck },
  { id: 'settings',     label: 'nav.settings', icon: Settings },
];

// ── LEGACY / ALT group REMOVED 2026-06-09 (Founder: keine Old-Pages mehr in der
// Sidebar). Die alten /o-Content-Pages erscheinen nicht mehr in der Navigation.

const founderItems = [
  { id: 'super-admin', label: 'Super Admin', icon: ShieldAlert, founderOnly: true },
  { id: 'founder-analytics', label: 'Analytics', icon: BarChart3, founderOnly: true },
  { id: 'super-admin/witnesses', label: 'Witness Network', icon: Eye, founderOnly: true },
  { id: 'super-admin/compliance-maturity', label: 'Maturity Registry', icon: ShieldCheck, founderOnly: true },
];

// Plan requirements per nav item — mirrors backend FEATURE_GATES (requirePlan.js).
// Drives Lock indicators in sidebar so users can see what requires an upgrade.
// NEW items: many are stubs (all access), others follow legacy tier mapping.
const PLAN_REQUIREMENTS: Record<string, RequiredPlan> = {
  // NEW items (clean routes, no n/ prefix) — mostly open access; some follow legacy tiers
  'overview': 'sandbox',  // all tiers
  'systems': 'scale',     // ops tier
  'connectors': 'scale', // ops tier
  'decision-log': 'scale',  // ops tier
  'review-queue': 'governance', // governance tier
  'anomalies': 'scale',    // ops tier
  'audit-chain': 'governance',
  'provenance': 'governance',
  'evidence-exports': 'governance',
  'incidents': 'governance',
  // GDPR Art. 17 is a mandatory data-subject right for ALL plan tiers — the
  // backend erasureRoutes are deliberately ungated (plan-gating a legal
  // obligation is potentially unlawful under GDPR Art. 12). Keep the nav item
  // visible + ungated for every tier to mirror the backend.
  'erasure': 'sandbox',
  'policy-engine': 'scale',
  'calibration-confidence': 'governance',
  'fairness-bias': 'governance',
  'compliance-overview': 'sandbox', // landing page, all tiers
  'eu-ai-act': 'governance',
  'gdpr': 'scale',
  'aml-gwg': 'governance',
  'beratung': 'governance',
  'dora': 'governance',
  'solvency-vag': 'governance',
  'eiopa': 'governance',
  'data-act-fida': 'governance',
  'analytics': 'governance',
  'reports': 'governance',

  // LEGACY items (original routes under o/ prefix)
  'o/mining':             'enterprise',
  'o/reports':            'governance',
  'o/activity-log':       'scale',
  'o/audit/chain':             'governance',
  'o/audit/exports':           'governance',
  'o/audit/insurance-feed':    'enterprise',
  'o/audit/replay':            'enterprise',
  'o/tee-attestation':         'custom',
  'o/erasure':                 'governance',
  'o/compliance/mapping':      'governance',
  'o/compliance/ropa':         'governance',
  'o/provider/technical-documentation': 'enterprise',
  'o/incidents':               'governance',
  'o/deployer':                'governance',
  'o/obligations':             'governance',
  'o/federated':               'custom',
  'o/provenance':              'governance',
  'o/advisory':                'governance',
  'o/privacy-budget':          'enterprise',
  // ADMIN (2026-06-13: auf Default-Routen migriert)
  'billing':                   'scale',
  'team':                      'scale',
  'settings':                  'sandbox',
  // activity-log: kein Lock (alle Tiers), wie zuvor o/activity-log
};

// Plan tier order (low → high) for the IA visibility filter. An item whose
// required plan is >1 tier above the user's plan is hidden from the nav;
// exactly one tier above shows as a locked teaser (Vanta-style focused nav).
const PLAN_ORDER = ['sandbox', 'scale', 'governance', 'enterprise', 'custom'];

// PLAN_LABEL was a local duplicate of PLAN_NAMES that drifted (missing
// "custom" key) — replace with the canonical map from PlanLock so every
// surface reads from one source. Tooltip copy is generated by
// planTooltip() helper which keeps wording in lockstep with the disabled
// buttons in IntegrationHub and PlanLock variants.

export function Sidebar({ isMobileOpen = false, onMobileClose, isDemoMode = false, onViewChange, demoCurrentView }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const logout = useLogout();

  const [sidebarWidthPx, setSidebarWidthPx] = useState<number>(() => {
    try { return Number(localStorage.getItem(SIDEBAR_WIDTH_KEY)) || DEFAULT_WIDTH; } catch { return DEFAULT_WIDTH; }
  });
  const sidebarRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  // Only set during drag; null = not dragging
  const [isDraggingCollapsed, setIsDraggingCollapsed] = useState<boolean | null>(null);

  // During drag use live value; otherwise derive from persisted width
  const effectiveCollapsed = !isDemoMode && !isMobileOpen && (
    isDraggingCollapsed !== null ? isDraggingCollapsed : sidebarWidthPx <= ICON_THRESHOLD
  );

  const handleDragStart = (e: React.MouseEvent) => {
    if (isDemoMode) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidthPx;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    // Disable width transition during drag — prevents per-frame animation lag
    if (sidebarRef.current) sidebarRef.current.style.transition = 'transform 200ms';

    const onMove = (ev: MouseEvent) => {
      const newWidth = startWidth + (ev.clientX - startX);
      const maxW = window.innerWidth * MAX_WIDTH_RATIO;
      const clamped = Math.round(Math.max(ICON_ONLY_WIDTH, Math.min(maxW, newWidth)));
      // Direct DOM — no React re-render per frame, smooth drag
      if (sidebarRef.current) sidebarRef.current.style.width = clamped + 'px';
      // Only re-render when crossing the threshold (icon ↔ labels switch)
      const nowCollapsed = clamped < ICON_THRESHOLD;
      setIsDraggingCollapsed(prev => prev === nowCollapsed ? prev : nowCollapsed);
    };

    const onUp = () => {
      dragCleanupRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Read final DOM width and compute snap target
      const domWidth = sidebarRef.current ? parseInt(sidebarRef.current.style.width, 10) || sidebarWidthPx : sidebarWidthPx;
      const snapped = domWidth < ICON_THRESHOLD ? ICON_ONLY_WIDTH : domWidth;
      // Restore transition THEN set snapped width directly — ensures animation always plays
      // (React won't re-render if snapped === current state, e.g. already at 48)
      if (sidebarRef.current) {
        sidebarRef.current.style.transition = '';
        sidebarRef.current.style.width = snapped + 'px';
      }
      setIsDraggingCollapsed(null);
      setSidebarWidthPx(snapped);
      try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(snapped)); } catch {}
    };

    dragCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const queryClient = useQueryClient();

  const { data: currentUser } = useCurrentUser();
  // CurrentUser type is loose at api boundary — narrow what we actually
  // read so the rest of the file doesn't sprinkle `as any` everywhere.
  const userView = currentUser as
    | { name?: string; email?: string; superAdmin?: boolean; ssoProvider?: string }
    | undefined;
  const displayName: string = userView?.name ?? 'User';
  const displayEmail: string = userView?.email ?? '';
  const initials: string = displayName.split(' ').filter((n: string) => n).map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // Plan-lock indicators — derive once per render
  const { data: billing } = useBilling();
  const userPlan: string =
    (billing as { subscription?: { plan?: string } } | undefined)?.subscription?.plan ?? 'sandbox';
  const lockInfo = (id: string): { locked: boolean; required?: RequiredPlan } => {
    const required = PLAN_REQUIREMENTS[id];
    if (!required) return { locked: false };
    return { locked: !planMeets(userPlan, required), required };
  };

  const { data: activeOrgRaw } = useOrganization();
  // Narrow once at the boundary so render JSX doesn't repeat the same
  // cast 6+ times (was: `(activeOrg as OrgRow | null)?.name` everywhere).
  const activeOrg: OrgRow | null = (activeOrgRaw ?? null) as OrgRow | null;
  const { data: userOrgsRaw = [] } = useUserOrgs();
  const userOrgs: OrgRow[] = userOrgsRaw as OrgRow[];
  const switchOrgMutation = useSwitchOrg();
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);
  const orgTriggerRef = useRef<HTMLButtonElement>(null);
  const orgPortalRef = useRef<HTMLDivElement>(null);
  const [orgDropdownPos, setOrgDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const orgMenuId = useId();
  const profileMenuId = useId();

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const profileDropdownPortalRef = useRef<HTMLDivElement>(null);
  const [profileDropdownPos, setProfileDropdownPos] = useState({ bottom: 0, left: 0 });

  const currentPath = location.pathname.split('/').filter(Boolean);
  const currentView = isDemoMode && demoCurrentView ? demoCurrentView : (currentPath[1] || 'overview');
  const currentFullView = isDemoMode && demoCurrentView
    ? demoCurrentView
    : (currentPath.slice(1).join('/') || 'overview');

  // Tracks window inner width so the drag-handle's aria-valuemax can stay
  // accurate after a window resize. Without this state the resize handler
  // would only re-render when the sidebar width itself was clamped down,
  // leaving aria-valuemax stale (e.g., user resizes window WIDER → no
  // setState → ARIA reports old smaller maximum).
  const [windowWidth, setWindowWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    function handleResize() {
      const maxW = window.innerWidth * MAX_WIDTH_RATIO;
      setWindowWidth(window.innerWidth);
      setSidebarWidthPx(prev => {
        const clamped = Math.min(prev, maxW);
        if (clamped === prev) return prev;
        // Persist the clamp so the next mount doesn't read a now-too-wide
        // value from localStorage and re-clamp on every load.
        try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(clamped))); } catch {}
        return clamped;
      });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up drag listeners if component unmounts mid-drag — and also
  // restore body styles. Normal mouseup-handled drag resets cursor +
  // userSelect, but if the component unmounts mid-drag (route change,
  // hot-reload), only the listener-cleanup ran → stuck col-resize
  // cursor + disabled text-selection app-wide (cat 13 memory-leak /
  // cleanup-completeness).
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      // Best-effort body-style restore — safe to run unconditionally
      // since the drag setter sets these to non-empty strings and
      // clearing to '' is the same behaviour as the onUp path.
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inTriggerArea  = userDropdownRef.current?.contains(target);
      const inPortalDropdown = profileDropdownPortalRef.current?.contains(target);
      if (!inTriggerArea && !inPortalDropdown) setIsUserDropdownOpen(false);
      const inOrgTrigger = orgTriggerRef.current?.contains(target);
      const inOrgPortal  = orgPortalRef.current?.contains(target);
      if (!inOrgTrigger && !inOrgPortal) setIsOrgDropdownOpen(false);
    }
    // ESC closes both dropdowns — was missing entirely pre-fix, leaving
    // keyboard users with no dismissal path other than tabbing away.
    // Also restore focus to the trigger button so keyboard users don't
    // get dumped to <body> after ESC (WAI-ARIA menu pattern: focus must
    // return to the invoking element on dismissal).
    function handleKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (isOrgDropdownOpen) {
        setIsOrgDropdownOpen(false);
        orgTriggerRef.current?.focus();
      }
      if (isUserDropdownOpen) {
        setIsUserDropdownOpen(false);
        profileTriggerRef.current?.focus();
      }
    }
    if (isOrgDropdownOpen || isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOrgDropdownOpen, isUserDropdownOpen]);

  const handleOrgButtonClick = () => {
    if (orgTriggerRef.current) {
      const rect = orgTriggerRef.current.getBoundingClientRect();
      setOrgDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
    }
    setIsOrgDropdownOpen(prev => !prev);
  };

  const handleSwitchOrg = (orgId: string) => {
    const activeId = getOrgId(activeOrg);
    if (!activeOrg || orgId === activeId || switchOrgMutation.isPending) return;
    switchOrgMutation.mutate(orgId, {
      onSuccess: () => {
        setIsOrgDropdownOpen(false);
        // SPA navigation + cache reset rather than `window.location.href`
        // — the prior full reload discarded the React-Query cache and
        // forced every dashboard query to refetch from a cold start.
        // invalidateQueries() is broad on purpose: org-scoped queries
        // MUST not serve stale data after an org switch.
        queryClient.invalidateQueries();
        navigate("/dashboard");
      },
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn("[Sidebar] switchOrg failed:", redactErrorForDev(err));
        }
        toast.error("Failed to switch organization. Please try again.");
      },
    });
  };

  const isFounder = !!userView?.superAdmin;

  // ── Progressive Disclosure (#13) ──────────────────────────────────────
  // Admin defaults COLLAPSED; Legacy/Alt group defaults COLLAPSED (old IA).
  const [adminCollapsed, setAdminCollapsed] = useState(true);
  const toggleAdminCollapsed = () => setAdminCollapsed(prev => !prev);
  useState(true);

  const handleNavClick = (id: string) => {
    if (isDemoMode && onViewChange) {
      onViewChange(id);
      if (onMobileClose) onMobileClose();
    } else {
      // Overview redirects to /dashboard root; others go to /dashboard/{id}
      const path = id === 'overview' ? '/dashboard' : `/dashboard/${id}`;
      navigate(path);
      if (onMobileClose) onMobileClose();
    }
  };

  // Sprint Final-Closer F-5: SAML Single Logout. When the current user
  // signed in via SAML AND their org has SAML SSO active, route through
  // /api/auth/saml/logout/initiate so the IdP also tears down its session.
  // Without SLO the user re-uses their cached IdP session on next /login →
  // they're auto-signed-back-in without challenge.
  const handleSignOut = async () => {
    const ssoProvider = userView?.ssoProvider;
    if (ssoProvider === 'saml') {
      try {
        const baseUrl = (import.meta.env.VITE_API_URL as string) || 'https://api.adjudon.com';
        const token = localStorage.getItem('adjudon_token');
        const res = await fetch(`${baseUrl}/api/auth/saml/logout/initiate`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const body = await res.json();
          // TokenManager.clearTokens() also removes the refresh-token AND
          // cancels the scheduled refresh-timer. Doing a bare removeItem
          // on 'adjudon_token' alone leaves the refresh-token + an in-flight
          // setTimeout that will try to silently re-authenticate the user
          // after the IdP-initiated SLO redirect.
          TokenManager.clearTokens();
          localStorage.removeItem('adjudon_active_workspace');
          if (body?.data?.logoutUrl) {
            // Hand off to the IdP — they'll redirect back to our SLO callback
            // which redirects to /login?slo=ok.
            window.location.href = body.data.logoutUrl;
            return;
          }
          // Fallback (no logoutUrl returned — backend couldn't build it).
          navigate('/login?slo=fallback');
          return;
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn("[Sidebar] SAML SLO initiate failed (falling back to local logout):", redactErrorForDev(err));
        }
        // Fall through to local logout
      }
    }
    logout.mutate(undefined, {
      onSuccess: () => {
        localStorage.removeItem('adjudon_active_workspace');
        navigate('/login');
      },
      onError: (err) => {
        if (import.meta.env.DEV) {
          console.warn("[Sidebar] logout failed (clearing local tokens anyway):", redactErrorForDev(err));
        }
        // Clear locally even if backend call fails. TokenManager also
        // tears down the refresh-token + scheduled refresh-timer so a
        // failed /logout doesn't leave the user silently re-authenticated
        // 14 minutes later.
        TokenManager.clearTokens();
        localStorage.removeItem('adjudon_active_workspace');
        navigate('/login');
      },
    });
  };

  const handleProfileClick = () => {
    if (effectiveCollapsed && profileTriggerRef.current) {
      const rect = profileTriggerRef.current.getBoundingClientRect();
      setProfileDropdownPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
    }
    setIsUserDropdownOpen(prev => !prev);
  };

  const navBtnClass = (isDisabled: boolean) => cn(
    "w-full flex items-center gap-2 px-2 transition-all border border-transparent",
    isMobileOpen ? "py-2 text-[13px]" : "py-1.5 text-sm",
    effectiveCollapsed && "justify-center px-0",
    isDisabled ? "opacity-40 cursor-not-allowed" : "hover:border-border hover:bg-surface-1",
  );

  // Active state — "raised paper card on stone" pattern.
  // Uses --surface-1 (pure white in light / near-black card surface
  // in dark) for stronger lightness break than page-canvas: the
  // active item visually pops forward like a piece of bright paper
  // resting on the recessed sidebar. Subtle shadow adds tactile depth.
  // Strong enough for at-a-glance "where am I?" in a dashboard
  // context, still editorial-restrained (only theme tokens, no new
  // colours, no saturated brand-tint).
  //
  // Cross-theme:
  //   Editorial-light:  white card on recessed neutral sidebar — clear lift
  //   Editorial-dark:   surface-1 dark card on near-black sidebar
  //                     — visible elevation, same metaphor inverted
  // Active-state vocabulary: neutral lifted-card background + sidebar
  // foreground text + a single brand accent on the 3px left-rule.
  // ONE brand signal per active item is enough — full brand-fill on
  // sidebar plus brand primary buttons plus editorial eyebrow plus
  // focus rings would over-saturate the page. The thin Maritime stripe
  // on the left edge says "I am here, in the brand" cleanly without
  // dominating. Mirrors Stripe / Linear / Notion sidebar discipline.
  const navBtnStyle = (isActive: boolean, isDisabled: boolean) =>
    isActive && !isDisabled ? {
      background: 'var(--surface-1)',
      borderColor: 'transparent',
      borderLeftColor: 'var(--brand)',
      borderLeftWidth: '3px',
      color: 'var(--brand)',
      fontWeight: 600,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    } : {};

  // ── IA reorg (2026-05-30): plan-aware visibility + shared item renderer ──
  // Hide a nav item whose required plan is >1 tier above the user's plan;
  // show exactly-one-tier-above as a locked teaser. Demo mode shows all.
  const itemVisible = (id: string): boolean => {
    if (isDemoMode) return true;
    const required = PLAN_REQUIREMENTS[id];
    if (!required) return true;
    const dist = PLAN_ORDER.indexOf(required) - PLAN_ORDER.indexOf(userPlan);
    return dist <= 1;
  };
  const visible = <T extends { id: string }>(items: readonly T[]): T[] =>
    items.filter((it) => itemVisible(it.id));

  // Single source for a nav button — used by every group so the active-state,
  // plan-lock, and a11y wiring stay identical. Active-state uses the full
  // path for slash-ids (e.g. 'audit/chain') and the first segment otherwise
  // (so 'agents' stays active on /agents/:id).
  // Labels can be i18n keys (e.g. 'nav.overview') or raw strings (legacy).
  const getLabel = (label: string): string => {
    if (label.startsWith('nav.')) {
      return t(label);
    }
    return label;
  };

  // Drill-down-Seiten OHNE eigenes Nav-Item (IA-Entscheid: Nav bleibt schlank) —
  // ihr Eltern-Item bleibt aktiv. Vorfälle = Drill-down der Compliance-Übersicht
  // (Karte „Melde-Vorfälle & Fristen" ist der Einstieg; Detail /incidents/:id erbt mit).
  const NAV_PARENT: Record<string, string> = { incidents: 'compliance-overview' };

  const renderNavButton = (item: { id: string; label: string; icon: typeof LayoutDashboard }) => {
    const Icon = item.icon;
    const effectiveView = NAV_PARENT[currentView] ?? currentView;
    const isActive = item.id.includes('/') ? currentFullView === item.id : effectiveView === item.id;
    if (isDemoMode && demoHiddenItems.includes(item.id)) return null;
    const isDisabled = isDemoMode && demoDisabledItems.includes(item.id);
    const { locked, required } = lockInfo(item.id);
    const displayLabel = getLabel(item.label);
    const tooltipContent = locked && required ? `${planTooltip(required, displayLabel)}` : displayLabel;
    return (
      <Tooltip key={item.id} content={tooltipContent} position="right" disabled={!effectiveCollapsed} className="w-full">
        <button
          onClick={() => !isDisabled && handleNavClick(item.id)}
          disabled={isDisabled}
          aria-current={isActive ? "page" : undefined}
          aria-label={locked && required ? `${displayLabel} — requires ${PLAN_NAMES[required]} plan or above` : undefined}
          className={navBtnClass(isDisabled)}
          style={navBtnStyle(isActive, isDisabled)}
        >
          <Icon className={cn("flex-shrink-0", effectiveCollapsed ? "w-4 h-4" : "w-3.5 h-3.5")} />
          {!effectiveCollapsed && (
            <>
              <span className="flex-1 min-w-0 text-left truncate">{displayLabel}</span>
              {locked && (
                <Lock className="w-3 h-3 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
            </>
          )}
        </button>
      </Tooltip>
    );
  };

  const sidebarWidth = isDemoMode
    ? 'var(--sidebar-width)'
    : (isMobileOpen ? 'var(--sidebar-width)' : sidebarWidthPx);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden animate-in fade-in-0 duration-200"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={cn(
          isDemoMode
            ? "flex flex-col h-dvh bg-sidebar border-r border-sidebar-border relative top-0 z-auto"
            : "flex flex-col h-dvh bg-sidebar border-r border-sidebar-border fixed lg:relative top-0 z-50 lg:z-auto transition-[transform,width] duration-200",
          !isDemoMode && (isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
        )}
        style={{ width: sidebarWidth }}
      >
        {/* Inner content wrapper — overflow:hidden here so drag handle can extend beyond */}
        <div className="flex flex-col h-full overflow-hidden">

        {/* Org Header (Desktop) / Workspace Switcher (Mobile) */}
        <div className="border-b border-border flex items-center flex-shrink-0" style={{ height: 'var(--header-height)' }}>
          <div className="flex items-center w-full">
            {effectiveCollapsed ? (
              <Tooltip content={activeOrg?.name ?? 'Organization'} position="right" className="w-full">
                <button
                  type="button"
                  onClick={handleOrgButtonClick}
                  ref={orgTriggerRef}
                  aria-label={`Organization ${activeOrg?.name ?? 'unknown'} — open menu to switch`}
                  aria-haspopup="menu"
                  aria-controls={orgMenuId}
                  aria-expanded={isOrgDropdownOpen}
                  className="w-full flex items-center justify-center hover:bg-surface-1 transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  style={{ height: 'var(--header-height)' }}
                >
                  <OrgAvatar org={activeOrg} size={28} />
                </button>
              </Tooltip>
            ) : (
              <button
                type="button"
                ref={orgTriggerRef}
                onClick={handleOrgButtonClick}
                aria-label={`Organization ${(activeOrg as OrgRow | null)?.name ?? 'unknown'} — open menu to switch`}
                aria-haspopup="menu"
                aria-controls={orgMenuId}
                aria-expanded={isOrgDropdownOpen}
                className="w-full flex items-center gap-2.5 px-3 hover:bg-surface-1 transition-colors motion-reduce:transition-none text-left focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                style={{ height: 'var(--header-height)' }}
              >
                <OrgAvatar org={activeOrg} size={28} />
                <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">{activeOrg?.name ?? '...'}</span>
                <ChevronDown aria-hidden="true" className={cn("w-3 h-3 text-muted-foreground transition-transform motion-reduce:transition-none flex-shrink-0", isOrgDropdownOpen && "rotate-180")} />
              </button>
            )}
          </div>
        </div>

        {/* Main Navigation — NEW IA (2026-05-30) ────────────────────────── */}
        <nav aria-label="Main navigation" className="flex-1 p-2 pb-20 lg:pb-4 space-y-0.5 overflow-y-auto">
          {/* Overview — always visible, pinned at top */}
          {visible(overviewItems).map(renderNavButton)}

          {/* Operate — daily operations, always expanded */}
          {visible(operateItems).length > 0 && (
            <>
              {!effectiveCollapsed && (
                <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 whitespace-nowrap">Operations</div>
              )}
              {visible(operateItems).map(renderNavButton)}
            </>
          )}

          {/* Audit & Proof — the wedge, always expanded */}
          {visible(auditProofItems).length > 0 && (
            <>
              {effectiveCollapsed ? (
                <Tooltip content="Audit & Proof" position="right" className="w-full">
                  <div className="my-2 mx-2 border-t border-border cursor-default" />
                </Tooltip>
              ) : (
                <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 whitespace-nowrap">Audit &amp; Proof</div>
              )}
              {visible(auditProofItems).map(renderNavButton)}
            </>
          )}

          {/* Governance — policy + calibration + fairness */}
          {visible(governanceItems).length > 0 && (
            <>
              {!effectiveCollapsed && (
                <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 whitespace-nowrap">Governance</div>
              )}
              {visible(governanceItems).map(renderNavButton)}
            </>
          )}

          {/* Compliance Hubs — domain-specific pages (collapsible) */}
          {visible(complianceHubItems).length > 0 && (
            <>
              {effectiveCollapsed ? (
                <Tooltip content="Compliance" position="right" className="w-full">
                  <div className="my-2 mx-2 border-t border-border cursor-default" />
                </Tooltip>
              ) : (
                <button
                  type="button"
                  onClick={() => {}}
                  className="w-full flex items-center gap-1 text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 hover:text-foreground transition-colors motion-reduce:transition-none whitespace-nowrap focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  aria-label={`Compliance section, ${visible(complianceHubItems).length} items`}
                >
                  <span>Compliance</span>
                  <span aria-hidden="true" className="text-[10px] ml-auto tabular-nums">{visible(complianceHubItems).length}</span>
                </button>
              )}
              {visible(complianceHubItems).map(renderNavButton)}
            </>
          )}

          {/* Insights / Analytics */}
          {visible(insightsItems).length > 0 && (
            <>
              {!effectiveCollapsed && (
                <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 whitespace-nowrap">Insights</div>
              )}
              {visible(insightsItems).map(renderNavButton)}
            </>
          )}

          {/* Admin — collapsible (default collapsed) */}
          {visible(adminItems).length > 0 && (
            <>
              {effectiveCollapsed ? (
                <Tooltip content="Admin" position="right" className="w-full">
                  <div className="my-2 mx-2 border-t border-border cursor-default" />
                </Tooltip>
              ) : (
                <button
                  type="button"
                  onClick={toggleAdminCollapsed}
                  className="w-full flex items-center gap-1 text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 hover:text-foreground transition-colors motion-reduce:transition-none whitespace-nowrap focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  aria-expanded={!adminCollapsed}
                  aria-label={`Admin section, ${visible(adminItems).length} items, ${!adminCollapsed ? 'expanded' : 'collapsed'}`}
                >
                  <ChevronRight aria-hidden="true" className={cn("w-3 h-3 transition-transform motion-reduce:transition-none", !adminCollapsed && "rotate-90")} />
                  <span>Admin</span>
                  <span aria-hidden="true" className="text-[10px] ml-auto tabular-nums">{visible(adminItems).length}</span>
                </button>
              )}
              {(!adminCollapsed || effectiveCollapsed) && visible(adminItems).map(renderNavButton)}
            </>
          )}

          {/* Founder section */}
          {isFounder && !isDemoMode && (
            <>
              {effectiveCollapsed
                ? <Tooltip content="Founder" position="right" className="w-full"><div className="my-2 mx-2 border-t border-border cursor-default" /></Tooltip>
                : <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider px-2 py-1.5 mt-3 whitespace-nowrap">Founder</div>
              }
              {founderItems.map(renderNavButton)}
            </>
          )}

        </nav>

        {/* User Profile */}
        <div className={isDemoMode ? "border-t border-sidebar-border" : "pb-20 lg:pb-0 border-t border-sidebar-border"}>
          <div className="relative" ref={userDropdownRef}>
            <Tooltip content={displayName} position="right" disabled={!effectiveCollapsed} className="w-full">
              <button
                type="button"
                ref={profileTriggerRef}
                onClick={handleProfileClick}
                aria-label={`User profile menu — ${displayName}`}
                aria-haspopup="menu"
                aria-controls={profileMenuId}
                aria-expanded={isUserDropdownOpen}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 hover:bg-surface-1 transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0",
                  isMobileOpen ? "py-3 text-[13px]" : "py-2.5",
                  effectiveCollapsed && "justify-center px-0"
                )}
              >
                <div className="w-7 h-7 bg-surface-2 border border-border flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <span className="text-[10px] font-semibold">{initials || 'U'}</span>
                </div>
                {!effectiveCollapsed && (
                  <>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-medium truncate">{displayName}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{displayEmail}</div>
                    </div>
                    <ChevronDown aria-hidden="true" className={cn("w-3 h-3 text-muted-foreground transition-transform motion-reduce:transition-none flex-shrink-0", isUserDropdownOpen && "rotate-180")} />
                  </>
                )}
              </button>
            </Tooltip>

            {isUserDropdownOpen && (() => {
              const profileMenuItems = (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { handleNavClick('profile'); setIsUserDropdownOpen(false); }}
                    className="w-full flex items-center gap-2 px-2 py-2 transition-colors motion-reduce:transition-none border-b border-border text-sm hover:bg-surface-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  >
                    <Settings aria-hidden="true" className="w-3.5 h-3.5" />
                    <span className="whitespace-nowrap">My Profile</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setIsUserDropdownOpen(false); setShowSignOutDialog(true); }}
                    className="w-full flex items-center gap-2 px-2 py-2 hover:bg-surface-2 transition-colors motion-reduce:transition-none text-foreground text-sm focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
                  >
                    <LogOut aria-hidden="true" className="w-3.5 h-3.5" />
                    <span className="whitespace-nowrap">Sign out</span>
                  </button>
                </>
              );
              return effectiveCollapsed ? createPortal(
                <div
                  ref={profileDropdownPortalRef}
                  id={profileMenuId}
                  role="menu"
                  aria-label="User profile menu"
                  style={{ position: 'fixed', bottom: profileDropdownPos.bottom, left: profileDropdownPos.left, zIndex: 9999 }}
                  className="w-48 adp-dropdown"
                >
                  {profileMenuItems}
                </div>,
                document.body
              ) : (
                <div
                  id={profileMenuId}
                  role="menu"
                  aria-label="User profile menu"
                  className="absolute bottom-full mb-1 left-0 right-0 adp-dropdown"
                >
                  {profileMenuItems}
                </div>
              );
            })()}
          </div>
        </div>

        </div>{/* end inner content wrapper */}

        {/* Drag Handle — desktop only, outside overflow:hidden, centered on
            border (6px in, 6px out). role="separator" + aria-orientation +
            keyboard handlers so screen-reader and keyboard-only users can
            also resize the sidebar. Pre-fix it was a `<div>` with onMouseDown
            only — invisible to AT and unusable from the keyboard. */}
        {!isDemoMode && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar — use left/right arrow keys, Home for icon-only, End for default width"
            aria-valuenow={Math.round(sidebarWidthPx)}
            aria-valuemin={ICON_ONLY_WIDTH}
            aria-valuemax={Math.round(windowWidth * MAX_WIDTH_RATIO)}
            tabIndex={0}
            onMouseDown={handleDragStart}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 32 : 8;
              const maxW = window.innerWidth * MAX_WIDTH_RATIO;
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const next = Math.max(ICON_ONLY_WIDTH, sidebarWidthPx - step);
                setSidebarWidthPx(next);
                try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(next)); } catch {}
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const next = Math.min(maxW, sidebarWidthPx + step);
                setSidebarWidthPx(next);
                try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(next)); } catch {}
              } else if (e.key === 'Home') {
                e.preventDefault();
                setSidebarWidthPx(ICON_ONLY_WIDTH);
                try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(ICON_ONLY_WIDTH)); } catch {}
              } else if (e.key === 'End') {
                e.preventDefault();
                setSidebarWidthPx(DEFAULT_WIDTH);
                try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(DEFAULT_WIDTH)); } catch {}
              }
            }}
            className="hidden lg:block absolute top-0 -right-1.5 w-3 h-full cursor-col-resize group focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
            style={{ zIndex: 10 }}
          >
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-brand/50 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-150 motion-reduce:transition-none" />
          </div>
        )}
      </div>

      {/* Sign Out Dialog */}
      <SignOutDialog
        isOpen={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
        onConfirm={handleSignOut}
      />

      {/* Org Dropdown Portal */}
      {isOrgDropdownOpen && createPortal(
        <div
          ref={orgPortalRef}
          id={orgMenuId}
          role="menu"
          aria-label="Switch organization"
          style={{ position: 'fixed', top: orgDropdownPos.top, left: orgDropdownPos.left, minWidth: orgDropdownPos.width, zIndex: 9999 }}
          className="adp-dropdown"
        >
          <div className="px-2 pt-2 pb-1">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">Organizations</span>
          </div>
          <div className="border-t border-border" aria-hidden="true" />
          {userOrgs.map((org, idx) => {
            const orgId = getOrgId(org);
            const activeId = getOrgId(activeOrg);
            const isActive = orgId === activeId;
            const isPending = switchOrgMutation.isPending;
            return (
              <button
                // Math.random() as a key is an anti-pattern: it changes on
                // every render, forcing React to unmount + remount the row,
                // killing focus + state. Fall back to index when both id
                // and name are missing so the key is at least stable per
                // position within the list.
                key={orgId ?? org.name ?? `org-${idx}`}
                type="button"
                role="menuitem"
                onClick={() => orgId && handleSwitchOrg(orgId)}
                disabled={isActive || isPending || !orgId}
                aria-current={isActive ? "true" : undefined}
                aria-busy={isPending && !isActive ? true : undefined}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-2 text-left text-sm transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0 disabled:cursor-not-allowed",
                  isActive ? "bg-[var(--brand-surface)] text-[var(--brand)] font-medium" : "hover:bg-surface-2"
                )}
              >
                <Building2 className={cn("w-3 h-3 flex-shrink-0", isActive ? "text-[var(--brand)] opacity-100" : "opacity-70")} aria-hidden="true" />
                <span className="flex-1 truncate min-w-0">{org.name}</span>
                {isActive && <Check className="w-3 h-3 text-[var(--brand)] flex-shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
          {!isDemoMode && (
            <>
              <div className="border-t border-border" aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                onClick={() => { setIsOrgDropdownOpen(false); setIsCreateOrgModalOpen(true); }}
                className="w-full flex items-center gap-2 px-2 py-2 text-left text-sm transition-colors motion-reduce:transition-none hover:bg-surface-2 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
              >
                <Plus className="w-3 h-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span className="text-muted-foreground">New Organization</span>
              </button>
            </>
          )}
        </div>,
        document.body
      )}

      {/* Create Org Modal */}
      <CreateOrgModal
        isOpen={isCreateOrgModalOpen}
        onClose={() => setIsCreateOrgModalOpen(false)}
      />
    </>
  );
}
